import { useCallback, useEffect, useState } from 'react';
import { listarPerfis } from '../../acesso';
import type { Falha, PerfilComMatriz } from '../../acesso';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { ErroDaLista, Esqueleto } from './Equipe';
import { AREAS, fraseDaArea } from './matriz';
import './acesso.css';

/**
 * TELA 33 — PERFIS E PERMISSÕES. "Esconder item na sidebar não é segurança."
 *
 * A matriz é a POLÍTICA DO SERVIDOR (`/api/perfis`), não uma tabela escrita aqui. O que esta
 * tela faz é só traduzir: para cada área, a primeira frase cujas capacidades o perfil tem. Se
 * o servidor tirar uma capacidade de um perfil, a célula muda sozinha — ou vira "—".
 *
 * A edição de perfil (33-B) só aparece quando o servidor permitir personalizar perfis; hoje a
 * matriz é a decidida pelo dono (23/09/2026) e é somente leitura.
 */

const ICONE_DO_PERFIL: Record<string, NomeDoIcone> = {
  admin_geral: 'shield',
  balconista: 'user',
  auditor: 'check',
};

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly perfis: readonly PerfilComMatriz[] };

export function Perfis() {
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });

  const carregar = useCallback(async () => {
    setEstado({ fase: 'carregando' });
    const r = await listarPerfis();
    setEstado(r.ok ? { fase: 'pronto', perfis: r.dados.perfis } : { fase: 'erro', falha: r.falha });
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (estado.fase === 'erro' && estado.falha.tipo === 'sem-acesso') {
    return <SemAcesso area="Perfis e permissões" />;
  }

  const perfis = estado.fase === 'pronto' ? estado.perfis : [];
  const conjuntos = perfis.map((p) => new Set(p.capacidades.map((c) => c.codigo)));

  return (
    <div className="ac ac-cheia">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Perfis e permissões</h1>
          <div className="ac-subtitulo">
            {estado.fase === 'pronto'
              ? `${numeroPorExtenso(perfis.length)} perfis em uso · princípio do menor privilégio necessário`
              : 'Princípio do menor privilégio necessário'}
          </div>
        </div>
        <span className="ac-nota">
          valores exibidos refletem a política configurada no servidor
        </span>
      </div>

      {estado.fase === 'erro' && (
        <ErroDaLista
          titulo="Não foi possível carregar os perfis"
          aoTentar={() => void carregar()}
        />
      )}

      {estado.fase !== 'erro' && (
        <div className="pf-perfis">
          {(estado.fase === 'carregando' ? [null, null, null] : perfis).map((p, i) =>
            p ? (
              <div className="pf-perfil" key={p.codigo}>
                <div className="pf-perfil-topo">
                  <Icone nome={ICONE_DO_PERFIL[p.codigo] ?? 'user'} tamanho={15} traco={1.7} />
                  <div className="pf-perfil-nome">{p.nome}</div>
                  <span className="pf-perfil-n" title="Pessoas ativas com este perfil">
                    {p.pessoas}
                  </span>
                </div>
                <div className="pf-perfil-texto">{semPonto(p.descricao ?? '')}</div>
              </div>
            ) : (
              <div className="pf-perfil pf-perfil-esqueleto" key={i} aria-busy="true" />
            ),
          )}
        </div>
      )}

      {estado.fase !== 'erro' && (
        <div className="ac-cartao ac-cartao-cheio">
          <div className="pf-matriz-cabeca">
            <div className="pf-matriz-titulo">O que cada perfil pode fazer</div>
            <span className="ac-espaco" />
            <span className="ac-nota">
              Esta matriz representa a política atualmente retornada pelo servidor
            </span>
          </div>
          {estado.fase === 'carregando' && <Esqueleto linhas={6} />}
          {estado.fase === 'pronto' && (
            <div className="ac-tabela-rolo">
              <table className="ac-tabela pf-matriz">
                <thead>
                  <tr>
                    <th>ÁREA</th>
                    {perfis.map((p) => (
                      <th key={p.codigo}>{p.nome.toUpperCase()}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {AREAS.map((a) => (
                    <tr key={a.area}>
                      <td className="pf-area">{a.area}</td>
                      {conjuntos.map((caps, i) => {
                        const frase = fraseDaArea(a, caps);
                        return (
                          <td key={perfis[i]?.codigo ?? i} className={frase ? undefined : 'pf-sem'}>
                            {frase ?? '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="ac-rodape-tabela">
            <span>
              {AREAS.length} áreas · {perfis.length || '—'} perfis
            </span>
            <span>
              Sem nome técnico de permissão · "—" é ausência de acesso, e o servidor recusa mesmo
              que a tela falhe
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function semPonto(t: string): string {
  return t.replace(/\.\s*$/, '');
}

function numeroPorExtenso(n: number): string {
  const nomes = ['Nenhum', 'Um', 'Dois', 'Três', 'Quatro', 'Cinco', 'Seis'];
  return nomes[n] ?? String(n);
}
